import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronRight, CalendarCheck } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/Dialog';
import { Checkbox } from '@/components/ui/Checkbox';
import RestrictedHolidays from '../../../../Attendance/RestrictedHolidays';
import { getHolidays, saveHoliday, updateHoliday, deleteHoliday, importHolidaysFile } from '../../../../../api/Attendance/leaves';
import { getBranches } from '../../../../../api/General/Company';
import type { LeavePolicyView, HolidayView, HolidayInput } from '../../../../../models/attendance';
import type { Branch } from '../../../../../models/general';
import { HolidayType, Recurrence } from '../../../../../models/attendance';

interface HolidaysLocationsTabProps {
  currentPolicy?: LeavePolicyView;
}

const HolidaysLocationsTab: React.FC<HolidaysLocationsTabProps> = ({ currentPolicy }) => {
  const { t } = useTranslation('leaves');
  const companyId = useSelector((state: any) => state.user?.company?.id);

  const [holidays, setHolidays] = useState<HolidayView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<HolidayView | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [restrictedExpanded, setRestrictedExpanded] = useState(false);
  const [apiBranches, setApiBranches] = useState<Branch[]>([]);
  const [newHoliday, setNewHoliday] = useState<HolidayInput>({
    name: '',
    description: '',
    date: '',
    holidayType: HolidayType.Public,
    locations: '',
    recurrence: Recurrence.Annually,
    isMandatory: true
  });

  useEffect(() => {
    loadHolidays();
  }, []);

  useEffect(() => {
    if (companyId) {
      getBranches(companyId).then(result => {
        if (!result?.errors?.length) setApiBranches(result.payload ?? []);
      }).catch(() => {});
    }
  }, [companyId]);

  const loadHolidays = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await getHolidays();
      if (result?.errors?.length) {
        const msg = result.errors[0]?.description || result.errors[0]?.message;
        setLoadError(msg || t('translation:somethingWentWrong', 'Failed to load holidays'));
      } else {
        setHolidays(result.payload ?? []);
      }
    } catch {
      setLoadError(t('translation:somethingWentWrong', 'Failed to load holidays'));
    } finally {
      setIsLoading(false);
    }
  };

  const holidayTypeOptions = [
    { value: HolidayType.Public, label: t('Public Holiday') },
    { value: HolidayType.National, label: t('National Holiday') },
    { value: HolidayType.Religious, label: t('Religious Holiday') },
    { value: HolidayType.Company, label: t('Company Holiday') }
  ];

  const recurringOptions = [
    { value: Recurrence.Annually, label: t('Annually (Gregorian)') },
    { value: Recurrence.Lunar, label: t('Lunar Calendar') },
    { value: Recurrence.OneTime, label: t('One-time only') }
  ];

  const locationOptions = apiBranches.map(b => ({ value: b.name, label: b.name }));

  // Data-differentiation colors — kept as-is per spec
  const getHolidayTypeColor = (type: HolidayType): string => {
    switch (type) {
      case HolidayType.Public: return 'bg-blue-100 text-blue-800 border-blue-200';
      case HolidayType.National: return 'bg-green-100 text-green-800 border-green-200';
      case HolidayType.Religious: return 'bg-purple-100 text-purple-800 border-purple-200';
      case HolidayType.Company: return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (date?: string): string => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString();
  };

  const parseLocations = (locations?: string): string[] => {
    if (!locations) return [];
    return locations.split(',').map(l => l.trim()).filter(Boolean);
  };

  const handleSaveHoliday = async (): Promise<void> => {
    if (!newHoliday?.name) return;
    setIsSaving(true);
    try {
      const input: HolidayInput = {
        name: newHoliday.name,
        description: newHoliday.description || undefined,
        date: newHoliday.date || undefined,
        holidayType: newHoliday.holidayType,
        locations: newHoliday.locations || undefined,
        recurrence: newHoliday.recurrence,
        isMandatory: newHoliday.isMandatory
      };
      const result = editingItem
        ? await updateHoliday(editingItem.id, input)
        : await saveHoliday(input);
      if (!result?.errors?.length) {
        await loadHolidays();
        resetForm();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditHoliday = (holiday: HolidayView): void => {
    setEditingItem(holiday);
    setNewHoliday({
      name: holiday.name,
      description: holiday.description ?? '',
      date: holiday.date ? holiday.date.split('T')[0] : '',
      holidayType: holiday.holidayType,
      locations: holiday.locations ?? '',
      recurrence: holiday.recurrence,
      isMandatory: holiday.isMandatory
    });
    setShowModal(true);
  };

  const handleDeleteHoliday = async (id: number): Promise<void> => {
    if (!window.confirm(t('Are you sure you want to delete this item?'))) return;
    try {
      const result = await deleteHoliday(id);
      if (!result?.errors?.length) {
        await loadHolidays();
      }
    } catch { /* handled by API layer */ }
  };

  const resetForm = (): void => {
    setShowModal(false);
    setEditingItem(null);
    setNewHoliday({
      name: '',
      description: '',
      date: '',
      holidayType: HolidayType.Public,
      locations: '',
      recurrence: Recurrence.Annually,
      isMandatory: true
    });
  };

  const handleImportHolidays = (): void => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.ics';
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement)?.files?.[0];
      if (file) {
        try {
          const result = await importHolidaysFile(file);
          if (!result?.errors?.length) {
            await loadHolidays();
          }
        } catch { /* handled by API layer */ }
      }
    };
    input.click();
  };

  const getLocationsForSelect = (): string[] => {
    if (!newHoliday.locations) return [];
    return newHoliday.locations.split(',').map(l => l.trim()).filter(Boolean);
  };

  const setLocationsFromSelect = (values: string[]): void => {
    setNewHoliday({ ...newHoliday, locations: values.join(', ') });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Holidays & Locations')}</h3>
          <p className="text-sm text-muted-foreground">{t('Manage public holidays and organizational observances by location')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" iconName="Upload" onClick={handleImportHolidays} data-testid="import-holidays-btn">
            {t('Import')}
          </Button>
          <Button variant="default" iconName="Plus" onClick={() => setShowModal(true)} data-testid="add-holiday-btn">
            {t('Add Holiday')}
          </Button>
        </div>
      </div>

      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Icon name="Info" size={20} className="text-warning mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('Holidays Configuration')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Manage public holidays and organizational observances. These dates will be automatically excluded from leave calculations.')}
            </p>
          </div>
        </div>
      </div>

      {currentPolicy && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy?.name}</span>
          </div>
        </div>
      )}

      {/* Statistics — data-differentiation colors kept */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{holidays?.filter(h => h?.holidayType === HolidayType.Public)?.length}</div>
          <div className="text-sm text-muted-foreground">{t('Public Holidays')}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{holidays?.filter(h => h?.holidayType === HolidayType.National)?.length}</div>
          <div className="text-sm text-muted-foreground">{t('National Holidays')}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">{holidays?.filter(h => h?.holidayType === HolidayType.Religious)?.length}</div>
          <div className="text-sm text-muted-foreground">{t('Religious Holidays')}</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-orange-600">{holidays?.filter(h => h?.holidayType === HolidayType.Company)?.length}</div>
          <div className="text-sm text-muted-foreground">{t('Company Holidays')}</div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="ms-3 text-sm text-muted-foreground">{t('Loading holidays...')}</span>
        </div>
      )}

      {/* Error State */}
      {!isLoading && loadError && (
        <div className="text-center py-12 bg-destructive/10 rounded-lg border border-destructive/20" role="alert" data-testid="holidays-load-error">
          <Icon name="AlertTriangle" size={48} className="mx-auto text-destructive/60 mb-4" />
          <h3 className="text-lg font-medium text-destructive mb-2">{t('translation:error', 'Error')}</h3>
          <p className="text-sm text-destructive/80 mb-4">{loadError}</p>
          <Button variant="default" iconName="RefreshCw" onClick={loadHolidays} data-testid="holidays-retry">
            {t('translation:retry', 'Retry')}
          </Button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !loadError && holidays.length === 0 && (
        <div className="text-center py-12 bg-muted/50 rounded-lg border border-border">
          <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground/60 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('No holidays configured')}</h3>
          <p className="text-sm text-muted-foreground mb-4">{t('Add holidays to automatically exclude them from leave calculations.')}</p>
          <Button variant="default" iconName="Plus" onClick={() => setShowModal(true)} data-testid="leave-holidays-add-empty">
            {t('Add Holiday')}
          </Button>
        </div>
      )}

      {/* Holidays List */}
      {!isLoading && holidays.length > 0 && (
        <div className="space-y-4">
          {holidays?.map((holiday) => (
            <div key={holiday?.id} className="bg-card border border-border rounded-lg p-6" data-testid="holiday-item">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div>
                    <h4 className="text-md font-semibold text-foreground">{holiday?.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(holiday?.date)}
                      {' '}&bull; {holiday?.recurrence !== Recurrence.OneTime ? t('Recurring') : t('One-time')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-sm rounded-full border ${getHolidayTypeColor(holiday?.holidayType)}`}>
                    {holidayTypeOptions?.find(opt => opt?.value === holiday?.holidayType)?.label}
                  </span>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  {holiday?.isMandatory && <span className="px-2 py-1 text-xs bg-destructive/10 text-destructive rounded-full">{t('Mandatory')}</span>}
                  <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEditHoliday(holiday)} data-testid="leave-holidays-edit" />
                  <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => handleDeleteHoliday(holiday?.id)} className="text-destructive hover:text-destructive/80" data-testid="leave-holidays-delete" />
                </div>
              </div>
              {holiday?.description && <p className="text-sm text-muted-foreground mb-3">{holiday?.description}</p>}
              {holiday?.locations && (
                <div>
                  <h5 className="text-sm font-medium text-muted-foreground mb-2">{t('Applicable Branches')}:</h5>
                  <div className="flex flex-wrap gap-2">
                    {parseLocations(holiday?.locations)?.map((loc, i) => <span key={i} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">{loc}</span>)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Restricted / Optional Holidays — Collapsible Section */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="restricted-holidays-section">
        <button
          onClick={() => setRestrictedExpanded(prev => !prev)}
          className="w-full flex items-center justify-between px-5 py-4 bg-muted/50 hover:bg-muted transition-colors text-start"
          data-testid="restricted-holidays-toggle"
        >
          <div className="flex items-center gap-3">
            <CalendarCheck className="w-5 h-5 text-indigo-600" />
            <div>
              <h3 className="text-base font-semibold text-foreground">{t('restrictedHolidaysSectionTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('restrictedHolidaysSectionDescription')}</p>
            </div>
          </div>
          {restrictedExpanded
            ? <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
            : <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          }
        </button>
        {restrictedExpanded && (
          <div className="p-5 border-t border-border">
            <RestrictedHolidays embedded />
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? t('Edit Holiday') : t('Add New Holiday')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Holiday Name')}</label>
              <Input type="text" value={newHoliday?.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewHoliday({ ...newHoliday, name: e?.target?.value })} placeholder={t('e.g., National Day')} data-testid="leave-holidays-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Date')}</label>
              <Input type="date" value={newHoliday?.date ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewHoliday({ ...newHoliday, date: e?.target?.value })} data-testid="leave-holidays-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Description')}</label>
              <textarea value={newHoliday?.description ?? ''} onChange={(e) => setNewHoliday({ ...newHoliday, description: e?.target?.value })} placeholder={t('Describe the holiday...')} rows={3} className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none" data-testid="leave-holidays-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Holiday Type')}</label>
                <Select value={newHoliday?.holidayType} onChange={(value: string) => setNewHoliday({ ...newHoliday, holidayType: value as HolidayType })} options={holidayTypeOptions} data-testid="leave-holidays-type" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Recurring')}</label>
                <Select value={newHoliday?.recurrence} onChange={(value: string) => setNewHoliday({ ...newHoliday, recurrence: value as Recurrence })} options={recurringOptions} data-testid="leave-holidays-recurrence" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Applicable Branches')}</label>
              <Select value={getLocationsForSelect()} onChange={(value: string[]) => setLocationsFromSelect(value)} options={locationOptions} multiple placeholder={t('Select branches')} data-testid="leave-holidays-locations" />
            </div>
            <div>
              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <Checkbox checked={newHoliday?.isMandatory} onCheckedChange={(checked: boolean) => setNewHoliday({ ...newHoliday, isMandatory: checked })} data-testid="leave-holidays-mandatory" />
                <span className="text-sm font-medium text-muted-foreground">{t('Mandatory holiday')}</span>
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm} data-testid="leave-holidays-modal-cancel">{t('Cancel')}</Button>
            <Button variant="default" onClick={handleSaveHoliday} disabled={!newHoliday?.name || isSaving} data-testid="holiday-save-btn">
              {isSaving ? t('Saving...') : (editingItem ? t('Update') : t('Add'))} {!isSaving && t('Holiday')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidaysLocationsTab;
